/**
 * AddEnvironmentModal — modal para acrescentar ambientes ao tenant pai.
 *
 * Backend: POST /v1/admin/tenants/{tenantId}/add-environment
 *   Body: { environments: ["DEV","SANDBOX",...] } (v1.32.1+)
 *
 * v1.32.1: alinhado com AddSubTenantModal — Checkbox.Group com 4 padroes
 *  + checkbox "Outro" que revela TextInput (max 8 chars, regex UPPERCASE)
 *  com chips de sugestao clicaveis (STG, SBOX, UAT, PERF, DR, LOAD, SANDBOX).
 *  Envs ja provisionados aparecem disabled com tooltip "Ja provisionado".
 *  UM job atomico independente de quantos envs forem selecionados.
 */

import { useMemo, useState } from 'react'
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Box,
  Checkbox,
  TextInput,
  Tooltip,
  Badge,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  awsOrgProvisioningApi,
  STANDARD_ENV_OPTIONS,
  ENV_SUGGESTIONS,
  ENV_NAME_REGEX,
  ENV_NAME_MAX_LENGTH,
  RESERVED_ENV_NAMES,
  type EnvName,
} from '@/api/awsOrgProvisioning'
import { handleProvisioningActionError } from './handleProvisioningActionError'

const CUSTOM_ENV_SENTINEL = '__CUSTOM__'

interface AddEnvironmentModalProps {
  tenantId: number
  opened: boolean
  onClose: () => void
  onSuccess?: () => void
  /** Ambientes que ja existem; viram checkboxes disabled com tooltip. */
  currentEnvs?: EnvName[]
}

function validateCustomEnv(
  value: string,
  takenSet: Set<string>,
): string | null {
  if (!value) return null
  const upper = value.toUpperCase()
  if (upper.length < 2) return 'Use no mínimo 2 caracteres.'
  if (upper.length > ENV_NAME_MAX_LENGTH) {
    return `Use no máximo ${ENV_NAME_MAX_LENGTH} caracteres.`
  }
  if (!ENV_NAME_REGEX.test(upper)) {
    return 'Apenas A-Z, 0-9, hífen e underscore. Comece com letra.'
  }
  if (STANDARD_ENV_OPTIONS.includes(upper as never)) {
    return `Use o checkbox de ${upper} acima — não precisa digitar.`
  }
  if (RESERVED_ENV_NAMES.includes(upper)) {
    return `Nome reservado pela AWS/convenção interna.`
  }
  if (takenSet.has(upper)) {
    return `${upper} já está provisionado neste tenant.`
  }
  return null
}

export function AddEnvironmentModal({
  tenantId,
  opened,
  onClose,
  onSuccess,
  currentEnvs = [],
}: AddEnvironmentModalProps) {
  const takenSet = useMemo(
    () => new Set(currentEnvs.map((e) => e.toUpperCase())),
    [currentEnvs],
  )

  const [envs, setEnvs] = useState<string[]>([])
  const [customEnv, setCustomEnv] = useState('')

  const reset = () => {
    setEnvs([])
    setCustomEnv('')
  }

  const customSelected = envs.includes(CUSTOM_ENV_SENTINEL)
  const customEnvUpper = customEnv.toUpperCase()
  const customEnvError = customSelected
    ? validateCustomEnv(customEnvUpper, takenSet)
    : null
  const customEnvValid =
    !customSelected || (customEnvUpper.length >= 2 && customEnvError === null)

  const standardSelected = envs.filter((e) => e !== CUSTOM_ENV_SENTINEL)
  const hasAtLeastOneEnv =
    standardSelected.length > 0 || (customSelected && customEnvValid)
  const formValid = hasAtLeastOneEnv && customEnvValid

  const buildEnvironments = (): EnvName[] => {
    const out: EnvName[] = [...standardSelected]
    if (customSelected && customEnvUpper) out.push(customEnvUpper)
    return out
  }

  const mutation = useMutation({
    mutationFn: () =>
      awsOrgProvisioningApi.addEnvironments(tenantId, buildEnvironments()),
    onSuccess: () => {
      const submitted = buildEnvironments()
      const envLabel =
        submitted.length === 1
          ? `Ambiente ${submitted[0]}`
          : `Ambientes ${submitted.join(', ')}`
      notifications.show({
        color: 'teal',
        title: 'Provisionamento agendado',
        message: `${envLabel} sera${submitted.length > 1 ? 'o' : ''} provisionado${submitted.length > 1 ? 's' : ''} em background.`,
      })
      reset()
      onSuccess?.()
      onClose()
    },
    onError: handleProvisioningActionError,
  })

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!mutation.isPending) {
          reset()
          onClose()
        }
      }}
      title="Adicionar ambiente ao tenant"
      data-testid="add-environment-modal"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Cria uma OU de ambiente sob o tenant e dispara o provisionamento
          completo (account AWS, IAM, secrets, SES, tags). Selecione um ou
          mais ambientes.
        </Text>

        <Box>
          <Text size="sm" fw={500} mb={4}>
            Ambientes
          </Text>
          <Checkbox.Group
            value={envs}
            onChange={(values) => setEnvs(values as string[])}
          >
            <Group gap="lg">
              {STANDARD_ENV_OPTIONS.map((e) => {
                const taken = takenSet.has(e)
                const node = (
                  <Checkbox
                    key={e}
                    value={e}
                    label={e}
                    disabled={taken}
                    data-testid={`add-environment-env-${e}`}
                  />
                )
                return taken ? (
                  <Tooltip
                    key={e}
                    label="Já provisionado neste tenant"
                    withArrow
                  >
                    <Box>{node}</Box>
                  </Tooltip>
                ) : (
                  node
                )
              })}
              <Checkbox
                value={CUSTOM_ENV_SENTINEL}
                label="Outro"
                data-testid="add-environment-env-custom"
              />
            </Group>
          </Checkbox.Group>
        </Box>

        {customSelected && (
          <Box
            p="sm"
            style={{
              border: '1px dashed var(--mantine-color-default-border)',
              borderRadius: 8,
              background: 'var(--mantine-color-default-hover)',
            }}
          >
            <Stack gap="xs">
              <TextInput
                label="Nome do ambiente custom"
                description={`UPPERCASE, A-Z e 0-9, max ${ENV_NAME_MAX_LENGTH} caracteres.`}
                placeholder="SANDBOX"
                value={customEnv}
                onChange={(e) =>
                  setCustomEnv(e.currentTarget.value.toUpperCase())
                }
                maxLength={ENV_NAME_MAX_LENGTH}
                error={customEnvError}
                data-testid="add-environment-custom-env-input"
              />
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  Sugestões:
                </Text>
                <Group gap={6}>
                  {ENV_SUGGESTIONS.map((s) => (
                    <Tooltip
                      key={s}
                      label={`Usar ${s}`}
                      withArrow
                      transitionProps={{ duration: 100 }}
                    >
                      <Badge
                        size="sm"
                        variant={customEnvUpper === s ? 'filled' : 'light'}
                        color="blue"
                        onClick={() => setCustomEnv(s)}
                        data-testid={`add-environment-suggestion-${s}`}
                        style={{
                          cursor: 'pointer',
                          textTransform: 'none',
                          fontWeight: 600,
                          letterSpacing: 0.3,
                        }}
                      >
                        {s}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Box>
            </Stack>
          </Box>
        )}

        <Group justify="flex-end" mt="sm">
          <Button
            variant="default"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!formValid}
            data-testid="add-environment-confirm"
          >
            Confirmar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
